import React, { useEffect, useState } from 'react';
import MainLayout from '../layout/MainLayout';
import advisoryApi, { AdvisoryPool, AdvisoryRequest, LANG_LABEL, apiError } from '../../services/advisoryApi';
import { CheckCircle2, Clock, XCircle, Lock } from 'lucide-react';
import '../advisory/advisory.scss';

const fmtDate = (s?: string | null) =>
  s ? new Date(s.replace(' ', 'T')).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export const StatusTag: React.FC<{ r: AdvisoryRequest }> = ({ r }) => {
  if (r.status === 'approved') return <span className="adv-tag adv-tag--ok"><CheckCircle2 size={14} /> Aprobada</span>;
  if (r.status === 'rejected') return <span className="adv-tag adv-tag--no"><XCircle size={14} /> No aprobada</span>;
  return <span className="adv-tag adv-tag--wait"><Clock size={14} /> En revisión</span>;
};

const BENEFITS = [
  'Resolver dudas puntuales sobre el programa.',
  'Abordar y analizar casos reales para tus sesiones con tu mentee.',
  'Preparar estructuras de conversación y definir objetivos.',
  'Recibir orientación experta para abordar conversaciones retadoras.',
];

const MentorAdvisory: React.FC = () => {
  const [pool, setPool] = useState<AdvisoryPool | null>(null);
  const [requests, setRequests] = useState<AdvisoryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () =>
    advisoryApi.getMine()
      .then(d => { setPool(d.pool); setRequests(d.requests || []); setLoadError(''); })
      .catch(e => setLoadError(apiError(e, 'No se pudo cargar la información.')))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const hasPending = requests.some(r => r.status === 'pending');
  const canRequest = !!pool && pool.available > 0 && !hasPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (detail.trim().length < 10) { setError('Cuéntanos con un poco más de detalle qué necesitas trabajar.'); return; }
    setBusy(true);
    try {
      await advisoryApi.create(detail.trim());
      setDetail('');
      await load();
    } catch (err) {
      setError(apiError(err, 'No se pudo enviar la solicitud. Intenta nuevamente.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MainLayout>
      <div className="advisory-page">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">MENTOR · EXECUTIVE MENTORING</div>
            <h1 className="heading-1">Reservar asesoría <em>personalizada</em></h1>
            <p className="adv-intro">
              Un espacio confidencial y virtual con el equipo de Cornerstone para potenciar tu rol de mentor.
              Cada solicitud es por 1 hora.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="adv-loading">Cargando…</div>
        ) : loadError ? (
          <div className="adv-loading">{loadError}</div>
        ) : (
          <div className="adv-grid">
            <div className="adv-col">
              <div className="adv-card">
                <h2 className="adv-card__title">¿Para qué sirve?</h2>
                <ul className="adv-list">
                  {BENEFITS.map(b => <li key={b}>{b}</li>)}
                </ul>
                <div className="adv-note"><Lock size={15} /> Es confidencial: tu mentee no ve tus solicitudes.</div>
              </div>

              <form className="adv-card" onSubmit={submit}>
                <h2 className="adv-card__title">Solicitar 1 hora</h2>
                {hasPending ? (
                  <p className="adv-muted">
                    Tu solicitud está en revisión. Te avisaremos por correo cuando sea aprobada; luego el equipo de
                    Cornerstone te contactará para coordinar la fecha y enviarte los accesos.
                  </p>
                ) : pool && pool.available < 1 ? (
                  <p className="adv-muted">Por ahora no quedan horas disponibles en la bolsa del programa.</p>
                ) : (
                  <>
                    <label className="adv-label" htmlFor="adv-detail">¿Qué te gustaría trabajar en la asesoría?</label>
                    <textarea
                      id="adv-detail"
                      className="adv-textarea"
                      rows={6}
                      maxLength={2000}
                      value={detail}
                      onChange={e => setDetail(e.target.value)}
                      placeholder="Ej.: Quiero preparar la próxima conversación con mi mentee sobre su plan de carrera y cómo abordar una situación retadora con su equipo."
                    />
                    {error && <div className="adv-error">{error}</div>}
                    <button type="submit" className="adv-btn adv-btn--primary adv-submit" disabled={busy || !canRequest}>
                      {busy ? 'Enviando…' : 'Enviar solicitud'}
                    </button>
                    <p className="adv-hint">Tu solicitud pasa por aprobación. Una vez aprobada, Cornerstone agenda contigo la sesión.</p>
                  </>
                )}
              </form>
            </div>

            <div className="adv-col">
              {pool && (
                <div className="adv-card adv-pool">
                  <div className="adv-pool__num"><em>{pool.remaining}</em> / {pool.total}</div>
                  <div className="adv-pool__lbl">horas disponibles en la bolsa del programa</div>
                  <div className="adv-bar"><span style={{ width: `${pool.total ? (pool.used / pool.total) * 100 : 0}%` }} /></div>
                </div>
              )}

              <div className="adv-card">
                <h2 className="adv-card__title">Mis solicitudes</h2>
                {requests.length === 0 ? (
                  <p className="adv-muted">Aún no has solicitado asesorías.</p>
                ) : (
                  <div className="adv-reqs">
                    {requests.map(r => (
                      <div className="adv-req" key={r.id}>
                        <div className="adv-req__head">
                          <StatusTag r={r} />
                          <span className="adv-req__date">{fmtDate(r.created_at)}</span>
                        </div>
                        <p className="adv-req__detail">{r.detail}</p>
                        {r.status === 'approved' && r.language && (
                          <p className="adv-req__meta">Idioma: {LANG_LABEL[r.language]} · Cornerstone te contactará para agendar.</p>
                        )}
                        {r.status === 'rejected' && r.decision_reason && (
                          <p className="adv-req__reason"><strong>Motivo:</strong> {r.decision_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MentorAdvisory;
